/**
 * Nosto-RecommendationClient.js
 *
 * Cliente general para obtener recomendaciones de Nosto usando
 * un placement ID.
 *
 * No renderiza HTML.
 * No sabe si el consumidor es un carrusel, grid o drawer.
 */
class NostoRecommendationClient {
    static requestCache = new Map();

    /**
     * Obtiene la recomendación asociada a un placement.
     *
     * @param {object} options
     * @param {string} options.placementId
     * @param {boolean} [options.includeTagging=true]
     * @param {boolean} [options.force=false]
     * @returns {Promise<NostoRecommendationResult>}
     */
    static async getRecommendation({
        placementId,
        productId = "",
        includeTagging = true,
        force = false,
    }) {
        const normalizedPlacementId =
            this.normalizePlacementId(
                placementId
            );

        const normalizedProductId =
            String(productId ?? "").trim();

        if (!normalizedPlacementId) {
            throw new Error(
                "[NostoRecommendationClient] placementId es obligatorio."
            );
        }

        const cacheKey = [
            normalizedPlacementId,
            normalizedProductId,
        ].join("::");

        if (
            !force &&
            this.requestCache.has(cacheKey)
        ) {
            return this.requestCache.get(
                cacheKey
            );
        }

        const requestPromise =
            this.requestPlacement({
                placementId:
                    normalizedPlacementId,
                productId:
                    normalizedProductId,
                includeTagging,
            });

        this.requestCache.set(
            cacheKey,
            requestPromise
        );

        try {
            return await requestPromise;
        } catch (error) {
            this.requestCache.delete(cacheKey);
            throw error;
        }
    }

    /**
     * Ejecuta la petición real a Nosto.
     *
     * @private
     */
    static async requestPlacement({
        placementId,
        productId,
        includeTagging,
    }) {
        await this.waitForNosto();

        return new Promise((resolve, reject) => {
            window.nostojs(async (api) => {
                try {
                    let request =
                        api.createRecommendationRequest({
                            includeTagging,
                        });

                    if (productId) {
                        request = request.setProducts([
                            {
                                product_id: productId,
                            },
                        ]);
                    }

                    const response = await request
                        .setElements([
                            placementId,
                        ])
                        .load();

                    console.log(
                        "[NostoRecommendationClient] Respuesta:",
                        response
                    );

                    console.log(
                        "[NostoRecommendationClient] Placement:",
                        placementId,
                        response?.recommendations?.[
                        placementId
                        ]
                    );

                    const recommendation =
                        response?.recommendations?.[
                        placementId
                        ];

                    if (!recommendation) {
                        resolve(
                            this.createEmptyResult({
                                placementId,
                                response,
                            })
                        );

                        return;
                    }

                    resolve(
                        this.normalizeRecommendation({
                            placementId,
                            recommendation,
                            response,
                        })
                    );
                } catch (error) {
                    reject(
                        new Error(
                            `[NostoRecommendationClient] Error cargando "${placementId}": ${error instanceof Error
                                ? error.message
                                : String(error)
                            }`
                        )
                    );
                }
            });
        });
    }

    /**
     * Convierte la respuesta de Nosto a una estructura estable
     * para el resto del tema.
     *
     * Nosto puede devolver campañas configuradas como JSON
     * o contenido HTML. Para nuestros componentes utilizaremos
     * preferentemente campañas con respuesta JSON.
     *
     * @private
     */
    static normalizeRecommendation({
        placementId,
        recommendation,
        response,
    }) {
        const html =
            typeof recommendation === "string"
                ? recommendation
                : recommendation?.html ?? "";

        const parsedHtml =
            this.parseRecommendationHtml(html);

        const structuredProducts =
            this.extractProducts(recommendation);

        const recommendedProducts =
            parsedHtml.recommendedProducts.length > 0
                ? parsedHtml.recommendedProducts
                : structuredProducts;

        const productIds =
            recommendedProducts
                .map((product) => product.productId)
                .filter(Boolean);

        const productHandles =
            recommendedProducts
                .map((product) => product.handle)
                .filter(Boolean);

        const uniqueProductIds = [
            ...new Set(
                productIds.map((productId) =>
                    String(productId)
                )
            ),
        ];

        return {
            placementId,

            resultId:
                recommendation?.resultId ??
                recommendation?.result_id ??
                recommendation?.div_id ??
                placementId,

            divId:
                recommendation?.divId ??
                recommendation?.div_id ??
                "",

            title:
                recommendation?.title ??
                recommendation?.name ??
                parsedHtml.title ??
                "",

            recommendedProducts,
            productIds: uniqueProductIds,
            productHandles,

            total: recommendedProducts.length,

            html,

            isEmpty:
                recommendedProducts.length === 0,

            rawRecommendation:
                recommendation,

            rawResponse:
                response,
        };
    }

    static parseRecommendationHtml(html) {
        if (
            typeof html !== "string" ||
            html.trim() === ""
        ) {
            return {
                title: "",
                recommendedProducts: [],
            };
        }

        const documentResponse =
            new DOMParser().parseFromString(
                html,
                "text/html"
            );

        const titleElement =
            documentResponse.querySelector(
                ".title, .nosto-header"
            );

        const recommendedProducts = Array.from(
            documentResponse.querySelectorAll(
                "[data-product-id], nosto-product[product-id]"
            )
        )
            .map((element) => {
                const productId =
                    element.dataset.productId ||
                    element.getAttribute("product-id") ||
                    "";

                const handle =
                    element.dataset.productHandle ||
                    element.getAttribute(
                        "data-product-handle"
                    ) ||
                    "";

                return {
                    productId: String(productId).trim(),
                    handle: String(handle).trim(),
                };
            })
            .filter(
                (product) =>
                    product.productId ||
                    product.handle
            );

        const uniqueProducts = [];
        const seen = new Set();

        recommendedProducts.forEach((product) => {
            const key =
                product.productId ||
                product.handle;

            if (!key || seen.has(key)) {
                return;
            }

            seen.add(key);
            uniqueProducts.push(product);
        });

        return {
            title:
                titleElement?.textContent
                    ?.replace(/\s+/g, " ")
                    .trim() ?? "",

            recommendedProducts:
                uniqueProducts,
        };
    }

    /**
     * Extrae productos tolerando variaciones comunes en
     * la estructura de respuesta.
     *
     * @private
     */
    static extractProducts(
        recommendation
    ) {
        if (!recommendation) {
            return [];
        }

        const possibleProducts =
            recommendation.products ??
            recommendation.items ??
            recommendation.result?.products ??
            recommendation.result?.items ??
            [];

        if (
            !Array.isArray(possibleProducts)
        ) {
            return [];
        }

        return possibleProducts
            .map((product, index) =>
                this.normalizeProduct(
                    product,
                    index
                )
            )
            .filter(Boolean);
    }

    /**
     * Genera un contrato común para todas las tarjetas.
     *
     * Conservamos `raw` porque Nosto puede tener campos
     * personalizados adicionales.
     *
     * @private
     */
    static normalizeProduct(
        product,
        index
    ) {
        if (
            !product ||
            typeof product !== "object"
        ) {
            return null;
        }

        const productId = String(
            product.productId ??
            product.product_id ??
            product.id ??
            ""
        );

        const handle =
            product.handle ??
            product.productHandle ??
            product.product_handle ??
            "";

        const url =
            product.url ??
            product.productUrl ??
            product.product_url ??
            "";

        return {
            index,
            productId,
            handle: String(handle).trim(),

            skuId: String(
                product.skuId ??
                product.sku_id ??
                product.selectedSkuId ??
                ""
            ),

            title:
                product.name ??
                product.title ??
                "",

            url,

            image:
                product.imageUrl ??
                product.image_url ??
                product.image ??
                product.thumbUrl ??
                "",

            alternateImage:
                product.alternateImageUrl ??
                product.alternate_image_url ??
                product.image2 ??
                "",

            price:
                product.price ??
                product.currentPrice ??
                null,

            compareAtPrice:
                product.listPrice ??
                product.compareAtPrice ??
                product.compare_at_price ??
                null,

            currency:
                product.currencyCode ??
                product.currency ??
                "",

            available:
                product.available !== false,

            tags:
                Array.isArray(product.tags)
                    ? product.tags
                    : [],

            attributionKey:
                product.attributionKey ??
                product.attribution_key ??
                "",

            raw: product,
        };
    }

    static createEmptyResult({
        placementId,
        response,
    }) {
        return {
            placementId,
            resultId: placementId,
            title: "",
            products: [],
            total: 0,
            html: "",
            isEmpty: true,
            rawRecommendation: null,
            rawResponse: response,
        };
    }

    static normalizePlacementId(
        placementId
    ) {
        return String(
            placementId ?? ""
        ).trim();
    }

    static waitForNosto({
        timeout = 10000,
        interval = 100,
    } = {}) {
        return new Promise((resolve, reject) => {
            if (typeof window.nostojs === "function") {
                resolve(window.nostojs);
                return;
            }

            const startedAt = Date.now();

            const timer = window.setInterval(() => {
                if (typeof window.nostojs === "function") {
                    window.clearInterval(timer);
                    resolve(window.nostojs);
                    return;
                }

                if (Date.now() - startedAt >= timeout) {
                    window.clearInterval(timer);

                    reject(
                        new Error(
                            "[NostoRecommendationClient] nostojs no estuvo disponible después de 10 segundos."
                        )
                    );
                }
            }, interval);
        });
    }

    /**
     * Elimina uno o todos los resultados almacenados.
     */
    static clearCache(
        placementId = null
    ) {
        if (placementId) {
            this.requestCache.delete(
                this.normalizePlacementId(
                    placementId
                )
            );

            return;
        }

        this.requestCache.clear();
    }
}

window.NostoRecommendationClient =
    NostoRecommendationClient;